import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../../components/layout/Layout';
import { getITContracts, createITContract, updateITContract, consumeContractHours, getClients } from '../../lib/api';
import { getITErrorMessage } from '../../lib/itErrors';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Shield, Plus, PencilSimple, Clock } from '@phosphor-icons/react';
import { toast } from 'sonner';

const CONTRACT_STATUSES = [
  { code: 'activo', name: 'Activo' },
  { code: 'vencido', name: 'Vencido' },
  { code: 'suspendido', name: 'Suspendido' },
  { code: 'cancelado', name: 'Cancelado' },
];

const EMPTY_CONTRACT = {
  client_id: '', name: '', hours_included: 0, start_date: '', end_date: '', status: 'activo', notes: '',
};

const statusColor = (status) => ({
  activo: 'bg-green-500/20 text-green-400',
  vencido: 'bg-red-500/20 text-red-400',
  suspendido: 'bg-yellow-500/20 text-yellow-400',
  cancelado: 'bg-gray-500/20 text-gray-400',
}[status] || 'bg-gray-500/20 text-gray-400');

const toList = (data) => (Array.isArray(data) ? data : (data?.items || []));
const inputClass = 'bg-[#12141a] border-gray-700 text-white';

export default function ContractsPage() {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hoursContract, setHoursContract] = useState(null);
  const [hoursForm, setHoursForm] = useState({ hours: 1, notes: '' });
  const [editing, setEditing] = useState(null);
  const [clients, setClients] = useState([]);
  const [filterClient, setFilterClient] = useState('all');
  const [form, setForm] = useState(EMPTY_CONTRACT);

  const loadContracts = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterClient !== 'all') params.client_id = filterClient;
      const res = await getITContracts(params);
      setContracts(toList(res.data));
    } catch (err) {
      toast.error(getITErrorMessage(err, 'Error al cargar los contratos'));
    } finally {
      setLoading(false);
    }
  }, [filterClient]);

  useEffect(() => { loadContracts(); }, [loadContracts]);
  useEffect(() => { getClients().then((res) => setClients(toList(res.data))).catch(() => {}); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_CONTRACT);
    setShowModal(true);
  };

  const openEdit = (contract) => {
    setEditing(contract);
    setForm({
      client_id: contract.client_id || '',
      name: contract.name || '',
      hours_included: contract.hours_included || 0,
      start_date: contract.start_date || '',
      end_date: contract.end_date || '',
      status: contract.status || 'activo',
      notes: contract.notes || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.client_id && !editing) return toast.error('Selecciona un cliente');
    if (!form.name.trim()) return toast.error('El nombre del contrato es obligatorio');
    const payload = {
      name: form.name.trim(),
      hours_included: Number(form.hours_included) || 0,
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      status: form.status,
      notes: form.notes.trim() || null,
    };
    setSaving(true);
    try {
      if (editing) {
        await updateITContract(editing._id, payload);
        toast.success('Contrato actualizado');
      } else {
        await createITContract({ ...payload, client_id: form.client_id });
        toast.success('Contrato creado');
      }
      setShowModal(false);
      loadContracts();
    } catch (err) {
      toast.error(getITErrorMessage(err, 'Error al guardar el contrato'));
    } finally {
      setSaving(false);
    }
  };

  const handleConsumeHours = async (event) => {
    event.preventDefault();
    if (!(Number(hoursForm.hours) > 0)) return toast.error('Indica las horas a registrar');
    try {
      const res = await consumeContractHours(hoursContract._id, { hours: Number(hoursForm.hours), notes: hoursForm.notes || null });
      const left = (res.data.hours_included || 0) - (res.data.hours_used || 0);
      toast.success(`Horas registradas. Restantes: ${left.toFixed(1)}`);
      setHoursContract(null);
      loadContracts();
    } catch (err) {
      toast.error(getITErrorMessage(err, 'No se pudieron registrar las horas'));
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Shield className="text-purple-400" size={28} /> Contratos SLA
            </h1>
            <p className="text-gray-400 text-sm mt-1">Contratos de soporte y bolsas de horas por cliente</p>
          </div>
          <Button onClick={openCreate} className="bg-[#E2B05E] hover:bg-[#c9993f] text-black">
            <Plus size={18} className="mr-2" /> Nuevo Contrato
          </Button>
        </div>

        <Select value={filterClient} onValueChange={setFilterClient}>
          <SelectTrigger className="w-full sm:w-[240px] bg-[#1a1d23] border-gray-700 text-white"><SelectValue placeholder="Cliente" /></SelectTrigger>
          <SelectContent className="bg-[#1a1d23] border-gray-700">
            <SelectItem value="all">Todos los clientes</SelectItem>
            {clients.map((client) => <SelectItem key={client._id} value={client._id}>{client.name || client.company_name}</SelectItem>)}
          </SelectContent>
        </Select>

        <div className="space-y-3">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#E2B05E]"></div>
            </div>
          ) : contracts.length === 0 ? (
            <div className="bg-[#1a1d23] border border-gray-800 rounded-lg text-center py-12 text-gray-500">
              <Shield size={48} className="mx-auto mb-3 opacity-50" />
              <p>No hay contratos registrados</p>
            </div>
          ) : contracts.map((contract) => {
            const included = Number(contract.hours_included) || 0;
            const used = Number(contract.hours_used) || 0;
            const percent = included > 0 ? Math.min(100, Math.round((used / included) * 100)) : 0;
            return (
              <div key={contract._id} className="bg-[#1a1d23] border border-gray-800 rounded-lg p-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1 flex-wrap">
                      <span className="text-white font-semibold">{contract.name}</span>
                      <Badge className={statusColor(contract.status)}>{contract.status}</Badge>
                    </div>
                    <div className="text-sm text-gray-400">
                      <span className="text-gray-300">{contract.client_name}</span>
                      {contract.start_date && ` · ${contract.start_date} → ${contract.end_date || 'sin fin'}`}
                    </div>
                    {included > 0 && (
                      <div className="mt-2 max-w-md">
                        <div className="flex justify-between text-xs text-gray-400 mb-1">
                          <span>{used.toFixed(1)} h usadas de {included} h</span>
                          <span>{percent}%</span>
                        </div>
                        <div className="h-2 rounded bg-[#12141a]">
                          <div className={`h-2 rounded ${percent >= 90 ? 'bg-red-500' : 'bg-[#3AB8D6]'}`} style={{ width: `${percent}%` }} />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="outline" onClick={() => { setHoursContract(contract); setHoursForm({ hours: 1, notes: '' }); }} className="border-gray-600 text-gray-300 text-xs">
                      <Clock size={14} className="mr-1" /> Registrar horas
                    </Button>
                    <Button size="sm" variant="ghost" aria-label="Editar contrato" onClick={() => openEdit(contract)}>
                      <PencilSimple size={16} className="text-[#E2B05E]" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="bg-[#1a1d23] border-gray-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-[#E2B05E]">{editing ? 'Editar Contrato' : 'Nuevo Contrato'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Cliente *</Label>
                  <Select value={form.client_id || undefined} disabled={Boolean(editing)} onValueChange={(value) => setForm((prev) => ({ ...prev, client_id: value }))}>
                    <SelectTrigger className={inputClass}><SelectValue placeholder="Seleccionar cliente" /></SelectTrigger>
                    <SelectContent className="bg-[#1a1d23] border-gray-700">
                      {clients.map((client) => <SelectItem key={client._id} value={client._id}>{client.name || client.company_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">Nombre *</Label>
                  <Input value={form.name} onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))} className={inputClass} />
                </div>
                <div>
                  <Label className="text-gray-300">Horas incluidas</Label>
                  <Input type="number" min="0" step="0.5" value={form.hours_included} onChange={(event) => setForm((prev) => ({ ...prev, hours_included: event.target.value }))} className={inputClass} />
                </div>
                <div>
                  <Label className="text-gray-300">Estado</Label>
                  <Select value={form.status} onValueChange={(value) => setForm((prev) => ({ ...prev, status: value }))}>
                    <SelectTrigger className={inputClass}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#1a1d23] border-gray-700">
                      {CONTRACT_STATUSES.map((status) => <SelectItem key={status.code} value={status.code}>{status.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-300">Inicio</Label>
                  <Input type="date" value={form.start_date} onChange={(event) => setForm((prev) => ({ ...prev, start_date: event.target.value }))} className={inputClass} />
                </div>
                <div>
                  <Label className="text-gray-300">Fin</Label>
                  <Input type="date" value={form.end_date} onChange={(event) => setForm((prev) => ({ ...prev, end_date: event.target.value }))} className={inputClass} />
                </div>
              </div>
              <div>
                <Label className="text-gray-300">Notas</Label>
                <Textarea value={form.notes} onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))} className={inputClass} rows={3} />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="border-gray-600 text-gray-300">Cancelar</Button>
                <Button type="submit" disabled={saving} className="bg-[#E2B05E] hover:bg-[#c9993f] text-black">{saving ? 'Guardando…' : 'Guardar'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog open={Boolean(hoursContract)} onOpenChange={(open) => !open && setHoursContract(null)}>
          <DialogContent className="bg-[#1a1d23] border-gray-700 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-[#E2B05E]">Registrar horas — {hoursContract?.name}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleConsumeHours} className="space-y-4">
              <div>
                <Label className="text-gray-300">Horas</Label>
                <Input type="number" min="0.5" step="0.5" value={hoursForm.hours} onChange={(event) => setHoursForm((prev) => ({ ...prev, hours: event.target.value }))} className={inputClass} />
              </div>
              <div>
                <Label className="text-gray-300">Notas</Label>
                <Textarea value={hoursForm.notes} onChange={(event) => setHoursForm((prev) => ({ ...prev, notes: event.target.value }))} className={inputClass} rows={2} />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <Button type="button" variant="outline" onClick={() => setHoursContract(null)} className="border-gray-600 text-gray-300">Cancelar</Button>
                <Button type="submit" className="bg-[#E2B05E] hover:bg-[#c9993f] text-black">Registrar</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
